import { Slider, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTruckModeLabel, materialStateEnum, stepModeEnum, truckGroupEnum, truckModeEnum } from '../../constants';
import { getMaterialStateColor } from '../../constants/colors';
import { isTruckContextEnabled } from '../../constants/helpers/truck';
import { useDailyResource } from '../../hooks/useDailyResource';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailyTransfer } from '../../services/apiSLTP/dailyReports/dailyTransfers';
import { DailyTruckPlanned } from '../../services/apiSLTP/dailyReports/dailyTrucksPlanned';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import DraggableCell from '../dnd/DraggableCell';
import DroppableCell from '../dnd/DroppableCell';
import {
    useCreateDailyTruckPlannedMutation,
    useDeleteDailyTruckPlannedMutation,
    useUpdateDailyTruckPlannedMutation,
} from '../../hooks/mutations/useDailyTrucksPlannedMutation';
import { DND_ITEM_TYPES } from '../../constants/dnd';
import { useCreateDailyTransferMutation } from '../../hooks/mutations/useDailyTransfersMutation';

interface TruckPlannedItemProps {
    truckPlanned: DailyTruckPlanned;
    index: number;
    activityId: string;
    activityReportTruckGroup?: number;
    stepMode: number;
    disabled: boolean;
    isSpecial?: boolean;
    refetch: () => void;
    onEdit: () => void;
    onAssign: () => void;
    onTransferEdit: (transfer: DailyTransfer) => void;
    onTransferMove: (fromLineId: string, fromItemId: string, toLineId: string) => void;
    activities: DataItem[];
}

const TruckPlannedItem: React.FC<TruckPlannedItemProps> = ({
    truckPlanned,
    index,
    activityId,
    stepMode,
    disabled,
    activityReportTruckGroup,
    isSpecial,
    refetch,
    onEdit,
    onAssign,
    onTransferEdit,
    onTransferMove,
}) => {
    const { t } = useTranslation();
    const [rank, setRank] = useState(truckPlanned.rank || 1);
    const itemType2 = DND_ITEM_TYPES.TRANSFERSPLANNED;
    const MIN = 1;
    const MAX = 20;
    const mode = truckPlanned.mode ?? -1;

    const { mutate: updateDailyTruckPlanned } = useUpdateDailyTruckPlannedMutation({ onSuccess: refetch });
    const { mutate: deleteDailyTruckPlanned } = useDeleteDailyTruckPlannedMutation({ onSuccess: refetch });
    const { mutate: createDailyTruckPlanned } = useCreateDailyTruckPlannedMutation({ onSuccess: refetch });
    const { mutate: createDailyTransfer } = useCreateDailyTransferMutation({ onSuccess: refetch });

    const { handleDelete, handleDuplicate } = useDailyResource<DailyTruckPlanned>({
        onMove: async (fromLineId, itemId, toLineId) => {
            updateDailyTruckPlanned({ DailyTruckPlannedId: itemId, data: { activityId: toLineId }, queries: { autoTruck: true } });
        },
        onDelete: async (itemId) => {
            deleteDailyTruckPlanned(itemId);
        },
        onDuplicate: async (truckPlanned) => {
            createDailyTruckPlanned(
                {
                    activityId,
                    trucksAmount: truckPlanned.trucksAmount,
                    departureLoadNatureId: truckPlanned.departureLoadNatureId?._id,
                    arrivalLoadNatureId: truckPlanned.arrivalLoadNatureId?._id,
                    departureDumpId: truckPlanned.departureDumpId?._id,
                    arrivalDumpId: truckPlanned.arrivalDumpId?._id,
                    departureQuarryId: truckPlanned.departureQuarryId?._id,
                    arrivalQuarryId: truckPlanned.arrivalQuarryId?._id,
                    departureSiteId: truckPlanned.departureSiteId?._id,
                    arrivalSiteId: truckPlanned.arrivalSiteId?._id,
                    mode: truckPlanned.mode,
                    ridesAmount: truckPlanned.ridesAmount,
                    truckTypeId: truckPlanned.truckTypeId?._id,
                    truckCompanyId: truckPlanned.truckCompanyId?._id,
                    observations: truckPlanned.observations,
                    truckId: truckPlanned.truckId?._id,
                },
                {
                    onSuccess: (data) => {
                        for (const transfer of truckPlanned.transfers) {
                            createDailyTransfer({
                                dailyTrucksPlannedId: data.data._id,
                                state: transfer.state,
                                machineId: transfer.machineId?._id,
                                quarryId: transfer.quarryId?._id,
                                rentalCompanyId: transfer.rentalCompanyId?._id,
                                rentalName: transfer.rentalName,
                                siteId: transfer.siteId?._id,
                                toolId: transfer.toolId?._id,
                            });
                        }
                    },
                }
            );
        },
    });

    useEffect(() => {
        setRank(truckPlanned.rank || 1);
    }, [truckPlanned.rank]);

    const updateRank = async (newRank: number) => {
        updateDailyTruckPlanned({ DailyTruckPlannedId: truckPlanned._id, data: { rank: newRank } }, { onSuccess: refetch });
    };

    const renderFromBlock = () =>
        (truckPlanned.departureDumpId || truckPlanned.departureLoadNatureId || truckPlanned.departureSiteId || truckPlanned.departureQuarryId) && (
            <div className="fx-c pad-l-3px border-radius-10px m-b-5px" style={{ backgroundColor: '#EEE' }}>
                {(truckPlanned.departureDumpId || truckPlanned.departureLoadNatureId) && (
                    <div className="one-line" style={{ fontStyle: 'italic' }}>
                        {truckPlanned.departureDumpId && `${capitalizeFirstLetter(t('dump'))} ${truckPlanned.departureDumpId?.designation} `}
                        {truckPlanned.departureLoadNatureId && `${truckPlanned.departureLoadNatureId?.designation}`}
                    </div>
                )}
                {(truckPlanned.departureSiteId || truckPlanned.departureQuarryId) && (
                    <div className="one-line">
                        <b>{t('from') + ' :  '}</b>
                        {truckPlanned.departureSiteId?.name || truckPlanned.departureQuarryId?.designation}
                    </div>
                )}
            </div>
        );

    const renderToBlock = () =>
        (truckPlanned.arrivalDumpId || truckPlanned.arrivalLoadNatureId || truckPlanned.arrivalSiteId || truckPlanned.arrivalQuarryId) && (
            <div className="fx-c pad-l-3px border-radius-10px m-b-5px" style={{ backgroundColor: '#EEE' }}>
                {(truckPlanned.arrivalDumpId || truckPlanned.arrivalLoadNatureId) && (
                    <div className="one-line" style={{ fontStyle: 'italic' }}>
                        {truckPlanned.arrivalDumpId && `${capitalizeFirstLetter(t('dump'))} ${truckPlanned.arrivalDumpId?.designation} `}
                        {truckPlanned.arrivalLoadNatureId && `${truckPlanned.arrivalLoadNatureId?.designation}` }
                    </div>
                )}
                {(truckPlanned.arrivalSiteId || truckPlanned.arrivalQuarryId) && (
                    <div className="one-line">
                        <b>{t('to') + ' : '}</b>
                        {truckPlanned.arrivalSiteId?.name || truckPlanned.arrivalQuarryId?.designation}
                    </div>
                )}
            </div>
        );

    const renderTransfers = () =>
        truckPlanned.mode === truckModeEnum.TRANSFER &&
        truckPlanned.transfers.map((transfer) => (
            <div
                key={transfer._id}
                className="fx-c fx-items-start border-radius-10px smFont pad-l-3px m-b-5px"
                style={{ backgroundColor: getMaterialStateColor(transfer.state), gap: 5 }}
            >
                <DraggableCell
                    dragIds={{ itemId: transfer._id, lineId: truckPlanned._id }}
                    index={index}
                    itemType={itemType2}
                    disabled={disabled}
                    disableMenu
                    state={transfer.state}
                    onDoubleClick={() => onTransferEdit(transfer)}
                >
                    <div className="fx-c fx-col gap-2 one-line-container">
                        {(transfer.machineId?.designation ||
                            transfer.toolId?.designation ||
                            transfer.rentalName ||
                            transfer.rentalCompanyId?.designation) && (
                            <div className="one-line">
                                {transfer.machineId?.designation ||
                                    transfer.toolId?.designation ||
                                    transfer.rentalName + ' ' + (transfer.rentalCompanyId?.designation || '')}
                            </div>
                        )}
                        <div className="one-line">
                            <b>{transfer.state === materialStateEnum.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                            {transfer.siteId?.name || transfer.quarryId?.designation}
                        </div>
                    </div>
                </DraggableCell>
            </div>
        ));

    return (
        <div className="fx-r gap-5px">
            <DraggableCell
                dragIds={{ itemId: truckPlanned._id, lineId: activityId }}
                index={index}
                itemType={DND_ITEM_TYPES.TRUCKSPLANNED}
                onDelete={() => handleDelete(truckPlanned._id)}
                onDuplicate={() => handleDuplicate(truckPlanned)}
                onDoubleClick={onEdit}
                disabled={disabled}
                menuItems={[
                    {
                        action: onAssign,
                        label: capitalizeFirstLetter(t('assignRide')),
                        shortcut: '',
                    },
                ]}
            >
                <DroppableCell
                    dropId={{ lineId: truckPlanned._id }}
                    moveCell={onTransferMove}
                    itemType={itemType2}
                    disabled={disabled || mode !== truckModeEnum.TRANSFER}
                >
                    <div className="h-100 one-line-container">
                        <div className="one-line font-w-700">
                            {stepMode === stepModeEnum.PLANNING && activityReportTruckGroup === truckGroupEnum.AMPLIROLL && (
                                <span className="border-radius-10px bg-white pad-l-3px pad-r-3px m-r-5px">
                                    {'№' + rank.toString().padStart(2, '0')}
                                </span>
                            )}
                            {(truckPlanned.trucksAmount !== 1 || isSpecial) && truckPlanned.trucksAmount + ''}
                            {typeof truckPlanned.initialTrucksAmount === 'number' &&
                                truckPlanned.initialTrucksAmount > 1 &&
                                isSpecial &&
                                '/' + truckPlanned.initialTrucksAmount + ''}
                            {` ${truckPlanned.truckTypeId?.designation || ''} ${truckPlanned.truckCompanyId?.designation || ''} ${
                                truckPlanned.truckId?.designation && truckPlanned.truckCompanyId?.isOwnCompany
                                    ? formatName(truckPlanned.truckId.userId?.firstName, truckPlanned.truckId.userId?.lastName)
                                    : ''
                            }`}
                        </div>
                        <div className="one-line" style={{ fontStyle: 'italic' }}>
                            {mode !== -1 && `${capitalizeFirstLetter(t(getTruckModeLabel(mode)))} `}
                            {truckPlanned.ridesAmount ? ` | ${truckPlanned.ridesAmount} ${t('ride(s)')}` : ''}
                        </div>

                        {isTruckContextEnabled(mode, 'departure') && renderFromBlock()}
                        {isTruckContextEnabled(mode, 'arrival') && renderToBlock()}
                        {isTruckContextEnabled(mode, 'transfer') && renderTransfers()}
                        {truckPlanned.observations && <div className="one-line">{truckPlanned.observations}</div>}

                        {!disabled && activityReportTruckGroup === truckGroupEnum.AMPLIROLL && (
                            <div className="fx-r fx-items-c gap-10px">
                                <Typography
                                    variant="body2"
                                    onClick={() => {
                                        setRank(MIN);
                                        updateRank(MIN);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {MIN}
                                </Typography>
                                <Slider
                                    size="small"
                                    value={rank}
                                    onChange={(e, value) => setRank(value as number)}
                                    onChangeCommitted={(e, value) => updateRank(value as number)}
                                    min={MIN}
                                    max={MAX}
                                    step={1}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                                <Typography
                                    variant="body2"
                                    onClick={() => {
                                        setRank(MAX);
                                        updateRank(MAX);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {MAX}
                                </Typography>
                            </div>
                        )}
                    </div>
                </DroppableCell>
            </DraggableCell>
        </div>
    );
};

export default TruckPlannedItem;
